import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HiOutlineHome, HiOutlineUsers, HiOutlineDocumentReport, HiOutlineBriefcase } from "react-icons/hi";
import { FaDollarSign, FaUserTie } from "react-icons/fa";
import logo from "../../../Assets/Uni_Logo.png";
import { SidebarContext } from "../SidebarContext";
import { UserContext } from "../../../Context/User";
import { EmployeeContext } from "../../../Context/Employee";
import { SalesContext } from "../../../Context/Sales";
import { getValuesFromLocalstorage } from "../../Helpers";
import { BarChart3, LayoutDashboard, StickyNote, Users } from "lucide-react";
import LOCAL_KEYS, { parseLocalStorageJSON } from "../../../helpers/localstorage";
import { LoginContext } from "../../../Context/Auth";
import { toast } from "react-toastify";

const Sidebar = () => {
  const { isExpanded, isMobile, isSidebarVisible, toggleSidebar } = useContext(SidebarContext);
  const { totalUsers, currentUserDetails } = useContext(UserContext);
  const { totalEmployees } = useContext(EmployeeContext);
  const { totalSales } = useContext(SalesContext);
  const profileDetails = parseLocalStorageJSON(LOCAL_KEYS.PROFILE_DETAILS);
  const {getProfilePhoto} = useContext(LoginContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [openSubMenu, setOpenSubMenu] = useState(null);
  
  // Check for password expiry
  const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED) || false;
  
  // Get user details to check account type
  const isMarketingExec = currentUserDetails?.acctType === "MKTG_EXEC";
  const isRecruiter = currentUserDetails?.acctType === "RECRUITER";
  const isAdmin = currentUserDetails?.acctType === "ADMIN";
  const isRecruiterOrAdmin = isRecruiter || isAdmin;
  // console.log("Sidebar - currentUserDetails:", currentUserDetails);
  // console.log("Sidebar - isMarketingExec:", isMarketingExec);
  // console.log("Sidebar - isRecruiterOrAdmin:", isRecruiterOrAdmin);
  const username = currentUserDetails?.username || currentUserDetails?.name || currentUserDetails?.firstName || "User";
  
  // Handle navigation click with password expiry check
  const handleNavClick = (e, item) => {
    if (isPasswordExpired && item.path !== "/updatePassword") {
      e.preventDefault();
      toast.error("Please change your password. Your password has expired.");
      navigate("/updatePassword");
      return false;
    }
    
    if (item.hasChildren) {
      e.preventDefault();
      setOpenSubMenu(openSubMenu === item.name ? null : item.name);
      // Navigate to the parent route
      navigate(item.path);
      return false;
    }
    
    if (location.pathname === item.path) {
      window.location.reload();
    }
    return true;
  };

  // Handle submenu item click
  const handleSubMenuClick = (e, subItem) => {
    e.preventDefault(); // Prevent default link behavior
    
    if (isPasswordExpired && subItem.path !== "/updatePassword") {
      toast.error("Please change your password. Your password has expired.");
      navigate("/updatePassword");
      return false;
    }
    
    navigate(subItem.path);
    // Keep the submenu open (don't change openSubMenu state)
    // Only close sidebar on mobile
    if (isMobile) {
      toggleSidebar();
    }
    return true;
  };
  
  // Define all menu items with correct order
  const allMenuItems = [
    { name: "Dashboard", path: "/", icon: <LayoutDashboard className="w-5 h-5" /> },
    {
      name: "Notes",
      path: "/notes",
      icon: <StickyNote className="w-5 h-5" />,
    },
    { 
      name: "Users", 
      path: "/users", 
      icon: <Users className="w-5 h-5" />,
      count: totalUsers,
      adminOnly: true 
    },
    { 
      name: "Sales", 
      path: "/sales", 
      icon: <FaDollarSign className="w-5 h-5" />,
      count: totalSales || 0,
      recruiterExcluded: true
    },
    { 
      name: "Consultants", 
      path: "/manage-employees", 
      icon: <FaUserTie className="w-5 h-5" />,
      count: totalEmployees,
      recruiterExcluded: true
    },
    // Remove Reports parent, add Submissions Table as main item
    {
      name: "Reports",
      path: "/reports/submissions",
      icon: <BarChart3 className="w-5 h-5" />,
      recruiterExcluded: true
    },
    {
      name: "Candidates",
      path: "/candidates",
      icon: <HiOutlineUsers className="w-5 h-5" />,
      recruiterOnly: true,
      showSeparatorBefore: true
    },
    {
      name: "Requirements",
      path: "/requirements",
      icon: <HiOutlineBriefcase className="w-5 h-5" />,
      recruiterOnly: true
    },
   
  ];
  
  // Filter items based on user role
  const menuItems = allMenuItems.filter(item => {
    // Admin: show all items (admin-only and recruiter-only)
    if (isAdmin) {
      return true;
    }
    // Recruiter: show only Dashboard and Requirements (exclude admin-only, marketing-exec-only, and recruiter-excluded items)
    if (isRecruiter) {
      return !item.adminOnly && !item.marketingExecOnly && !item.recruiterExcluded;
    }
    // Marketing executives: exclude admin-only and recruiter-only items
    if (isMarketingExec) {
      return !item.adminOnly && !item.recruiterOnly;
    }
    // Other users: exclude admin-only and recruiter-only items
    return !item.adminOnly && !item.recruiterOnly;
  });



  // Auto-open submenu if we're on a child page
  useEffect(() => {
    const currentPath = location.pathname;
    const reportsItem = menuItems.find(item => item.name === "Reports");
    if (reportsItem?.hasChildren) {
      const isOnChildPage = reportsItem.children.some(child => child.path === currentPath);
      if (isOnChildPage) {
        setOpenSubMenu("Reports");
      }
    }
  }, [location.pathname, menuItems]);

  // If mobile and sidebar is not visible, don't render
  if (isMobile && !isSidebarVisible) {
    return null;
  }

  return (
    <>
      {isMobile && isSidebarVisible && (
        <div className="sidebar-overlay" onClick={toggleSidebar} />
      )}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: isExpanded ? '260px' : '70px',
        backgroundColor: 'white',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        zIndex: isMobile ? 50 : 30,
        transition: 'all 0.3s ease',
        overflowY: 'auto',
        overflowX: 'hidden',
        borderRight: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        transform: isMobile && !isSidebarVisible ? 'translateX(-100%)' : 'translateX(0)',
      }}>
        <div className="flex items-center px-6 py-6">
          <img src={logo} alt="Logo" className="h-8 w-7 object-contain" />
          {isExpanded && (
            <h2 className="text-xl font-bold text-slate-800 tracking-wide ml-3">UniMark</h2>
          )}
        </div>
        <nav className={`flex-1 px-4 pb-4 space-y-1 overflow-y-auto ${isExpanded ? 'px-4' : ''}`}>
          {menuItems.map((item, index) => (
            <div key={item.name}>
              {item.showSeparatorBefore && isAdmin && (
                <div className="my-3">
                  {isExpanded ? (
                    <div className="flex items-center px-4 py-2">
                      <div className="flex-1 border-t border-gray-200"></div>
                      <span className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Recruitment</span>
                      <div className="flex-1 border-t border-gray-200"></div>
                    </div>
                  ) : (
                    <div className="border-t border-gray-200"></div>
                  )}
                </div>
              )}
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `flex ${isExpanded ? 'items-center' : 'justify-center items-center'} py-3 px-4 text-sm font-medium rounded-lg transition-colors ${
                    isActive || (item.hasChildren && item.children?.some(child => location.pathname === child.path))
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`
                }
                title={!isExpanded ? item.name : ''}
                end={!item.hasChildren}
                onClick={(e) => handleNavClick(e, item)}
              >
                <span className={isExpanded ? "mr-3" : ""}>{item.icon}</span>
                {isExpanded && (
                  <>
                    <span>{item.name}</span>
                    {typeof item.count === 'number' && item.count > 0 && (
                      <span className="ml-auto bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                        {item.count}
                      </span>
                    )}
                    {item.hasChildren && (
                      <span className="ml-auto">
                        {openSubMenu === item.name ? "▼" : "►"}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
              
              {/* Submenu */}
              {item.hasChildren && openSubMenu === item.name && isExpanded && (
                <div className="ml-6 mt-1 space-y-1">
                  {item.children.map((subItem) => (
                    <NavLink
                      key={subItem.name}
                      to={subItem.path}
                      className={({ isActive }) =>
                        `flex items-center py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
                          isActive
                            ? "bg-blue-100 text-blue-700"
                            : "text-gray-600 hover:bg-gray-50"
                        }`
                      }
                      onClick={(e) => handleSubMenuClick(e, subItem)}
                    >
                      <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                      {subItem.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        
        {/* User Profile Section */}
        <div className="mt-auto border-t border-gray-200 p-4">
          <div className="flex items-center">
            <div className="relative">
              <img 
                src={getProfilePhoto().src} 
                alt="Profile" 
                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = getProfilePhoto().fallback;
                }}
              />
            </div>
            
            {isExpanded && (
              <div className="ml-3 overflow-hidden">
                <p class="text-sm font-medium text-gray-900">
                  {currentUserDetails?.firstName && currentUserDetails?.lastName 
                    ? `${currentUserDetails.firstName} ${currentUserDetails.lastName}` 
                    : username}
                </p>
                <p className="text-xs font-medium text-gray-500">
                  {profileDetails?.role || profileDetails?.acctType || "Talent Acquisition"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;